import { Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router";
import {
  USER_ROLES,
  USER_STORE_SELECTORS,
  type UserRolesValues,
  useUserStore,
} from "@/entities/user";
import { SpinnerStyled } from "@/components/atoms";
import { RootLayout } from "./layouts";

export const ROUTES = {
  main: "/",
  constructor: "/bb/:id/:tab?",
  constructorCreate: "/bb/create",
  monitoring: "/monitoring",
  report: "/monitoring/:id",
  statistic: "/statistic/:id?",
};

function Error() {
  return <div>Failed to load page</div>;
}

const ProtectedRoute = ({ allowedRoles }: { allowedRoles: UserRolesValues[] }) => {
  const { t } = useTranslation();
  const roles = useUserStore(USER_STORE_SELECTORS.roles);
  const devSettings = useUserStore(USER_STORE_SELECTORS.devSettings);
  const hasAccess = allowedRoles.some((role) => roles.includes(role));
  const hasDevAccess = devSettings?.devToken != undefined;
  if (!hasAccess && !hasDevAccess) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div>{t("common.error.noAccess.message", { defaultValue: "Нет доступа" })}</div>
      </div>
    );
  }

  return <Outlet />;
};

const loader = (
  <Flex w="100%" h="100%" align="center" justify="center">
    <SpinnerStyled />
  </Flex>
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<RootLayout />}>
        <Route
          element={
            <ProtectedRoute
              allowedRoles={[
                USER_ROLES.BillboardManagerReadSuper,
                USER_ROLES.BillboardManagerReadAdmin,
              ]}
            />
          }
        >
          <Route
            hydrateFallbackElement={loader}
            path="/"
            lazy={async () => {
              try {
                const { MainPage } = await import("@/pages/main-page/main-page");
                return { Component: MainPage };
              } catch (e) {
                console.error("Error: ", e);

                return { Component: Error };
              }
            }}
          />
        </Route>
        <Route
          element={
            <ProtectedRoute
              allowedRoles={[
                USER_ROLES.BillboardManagerCreateSuper,
                USER_ROLES.BillboardManagerReadAdmin,
              ]}
            />
          }
        >
          <Route
            path={ROUTES.constructorCreate}
            hydrateFallbackElement={loader}
            lazy={async () => {
              try {
                const { BBCreatePage } = await import("@/pages/bb-create-page");
                return { Component: BBCreatePage };
              } catch (e) {
                console.error("Error: ", e);

                return { Component: Error };
              }
            }}
          />
        </Route>
        <Route
          element={
            <ProtectedRoute
              allowedRoles={[
                USER_ROLES.BillboardManagerReadSuper,
                USER_ROLES.BillboardManagerReadAdmin,
              ]}
            />
          }
        >
          <Route
            path={ROUTES.constructor}
            hydrateFallbackElement={loader}
            lazy={async () => {
              try {
                const { ConstructorPage } = await import("@/pages/constructor-page");
                return { Component: ConstructorPage };
              } catch (e) {
                console.error("Error: ", e);

                return { Component: Error };
              }
            }}
          />
        </Route>
        <Route
          element={
            <ProtectedRoute
              allowedRoles={[
                USER_ROLES.BillboardMonitoringReadSuper,
                USER_ROLES.BillboardManagerReadAdmin,
              ]}
            />
          }
        >
          <Route
            path={ROUTES.monitoring}
            hydrateFallbackElement={loader}
            lazy={async () => {
              try {
                const { MonitoringPage } = await import("@/pages/monitoring-page");
                return { Component: MonitoringPage };
              } catch (e) {
                console.error("Error: ", e);

                return { Component: Error };
              }
            }}
          />
        </Route>
        <Route
          element={
            <ProtectedRoute
              allowedRoles={[
                USER_ROLES.BillboardMonitoringReadSuper,
                USER_ROLES.BillboardManagerReadAdmin,
              ]}
            />
          }
        >
          <Route
            path={ROUTES.report}
            hydrateFallbackElement={loader}
            lazy={async () => {
              try {
                const { ReportPage } = await import("@/pages/report-page");
                return { Component: ReportPage };
              } catch (e) {
                console.error("Error: ", e);
                return { Component: Error };
              }
            }}
          />
        </Route>

        <Route
          path={ROUTES.statistic}
          hydrateFallbackElement={loader}
          lazy={async () => {
            try {
              const { StatisticPage } = await import("@/pages/statistic-page");
              return { Component: StatisticPage };
            } catch (e) {
              console.error("Error: ", e);

              return { Component: Error };
            }
          }}
        />
        <Route
          element={
            <ProtectedRoute
              allowedRoles={[
                USER_ROLES.BillboardManagerReadSuper,
                USER_ROLES.BillboardManagerReadAdmin,
              ]}
            />
          }
        >
          <Route
            path="/*"
            lazy={async () => {
              try {
                const { MainPage } = await import("@/pages/main-page/main-page");
                return { Component: MainPage };
              } catch (e) {
                console.error("Error: ", e);
                return { Component: Error };
              }
            }}
          />
        </Route>
      </Route>
    </>,
  ),
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
